/**
 * Shared search + errors-only filter bar for dev log views.
 */
import type { LogLevel } from "@memes/shared";

const LEVELS: (LogLevel | "")[] = ["", "error", "fatal", "warn", "info", "debug", "trace"];

export function LogFilterBar({
  search,
  onSearchChange,
  searchPlaceholder = "Search logs…",
  searchRef,
  errorsOnly,
  onErrorsOnlyChange,
  level,
  onLevelChange,
  showLevelSelect = false,
  source,
  onSourceChange,
  showSourceSelect = false,
  module,
  onModuleChange,
  showModuleInput = false,
  children,
}: {
  search: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  searchRef?: React.RefObject<HTMLInputElement | null>;
  errorsOnly: boolean;
  onErrorsOnlyChange: (value: boolean) => void;
  level?: LogLevel | "";
  onLevelChange?: (value: LogLevel | "") => void;
  showLevelSelect?: boolean;
  source?: "" | "server" | "client";
  onSourceChange?: (value: "" | "server" | "client") => void;
  showSourceSelect?: boolean;
  module?: string;
  onModuleChange?: (value: string) => void;
  showModuleInput?: boolean;
  children?: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="flex flex-wrap gap-2">
      <input
        ref={searchRef}
        type="search"
        className="input min-w-[200px] flex-1 text-sm"
        placeholder={searchPlaceholder}
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
      />
      <button
        type="button"
        className={`btn text-sm ${errorsOnly ? "bg-red-600 text-white hover:bg-red-700" : ""}`}
        onClick={() => onErrorsOnlyChange(!errorsOnly)}
        aria-pressed={errorsOnly}
      >
        Errors only
      </button>
      {showLevelSelect && onLevelChange ? (
        <select
          className="input text-sm"
          value={errorsOnly ? "" : level}
          disabled={errorsOnly}
          onChange={(e) => onLevelChange(e.target.value as LogLevel | "")}
        >
          {LEVELS.map((l) => (
            <option key={l || "all"} value={l}>
              {l || "All levels"}
            </option>
          ))}
        </select>
      ) : null}
      {showSourceSelect && onSourceChange ? (
        <select
          className="input text-sm"
          value={source}
          onChange={(e) => onSourceChange(e.target.value as "" | "server" | "client")}
        >
          <option value="">All sources</option>
          <option value="server">Server</option>
          <option value="client">Client</option>
        </select>
      ) : null}
      {showModuleInput && onModuleChange ? (
        <input
          className="input text-sm"
          placeholder="Module"
          value={module}
          onChange={(e) => onModuleChange(e.target.value)}
        />
      ) : null}
      {children}
    </div>
  );
}
